var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
import * as React from 'react';
import Logo from '@doordash/component-logo';
import { Icon, Stack, StackChildren, Text } from '@doordash/design-language';
import Button from '@doordash/component-button';
import { QuizScreenContainer, FullScreenContainer, BackContainer, ActionAndProgressContainer, BackAndProgressContainer, } from './QuizV2Styles';
import { isEmailFormatValid } from '../../../utils/validateEmailHelper';
import { useMediaQuery } from '../../Common/MediaQueryProvider';
import track from '../../../utils/track';
import { QuizEvents } from '../../../constants/events';
import { QuizPageStrings } from './QuizV2Strings';
import { EmailInputSection, ProgressBar, QuestionContainer } from './components';
import { QuestionsData, QuizQuestionId, WhereIsTeamResponseId, } from './QuizV2PageData';
import RecommendationView from './RecommendationView';
var PRODUCT_OPTIONS_VERSION = 'quizV2';
var Step;
(function (Step) {
    Step["questionPage1"] = "questionPage1";
    Step["questionPage2"] = "questionPage2";
    Step["questionEmailInput"] = "questionEmailInput";
    Step["recommendation"] = "recommendation";
})(Step || (Step = {}));
export var QuizV2 = function () {
    var Questions = [
        Step.questionPage1,
        Step.questionPage2,
        Step.questionEmailInput,
    ];
    var QuizSteps = __spread(Questions, [Step.recommendation]);
    var _a = __read(React.useState({}), 2), questionResponses = _a[0], setQuestionResponses = _a[1];
    var _b = __read(React.useState(0), 2), quizStepIndex = _b[0], setQuizStepIndex = _b[1];
    var _c = __read(React.useState(''), 2), emailError = _c[0], setEmailError = _c[1];
    var isMobile = useMediaQuery().isMobile;
    React.useEffect(function () {
        // on page load
        track(QuizEvents.PAGE_VIEW, {
            page_version: PRODUCT_OPTIONS_VERSION,
        });
    }, []);
    var getButtonText = function () {
        if (isLastQuestion()) {
            return QuizPageStrings.actionButton.finish();
        }
        return QuizPageStrings.actionButton.continue();
    };
    var isOnRecommendationStep = QuizSteps[quizStepIndex] === Step.recommendation;
    var isLastQuestion = function () {
        var lastQuestion = Questions[Questions.length - 1];
        return QuizSteps[quizStepIndex] === lastQuestion;
    };
    var hasBack = function () {
        return quizStepIndex > 0 && !isOnRecommendationStep;
    };
    var onBackClick = function () {
        track(QuizEvents.BACK_CLICK, {
            // current question page
            question: QuizSteps[quizStepIndex],
        });
        return setQuizStepIndex(quizStepIndex - 1);
    };
    var canMoveForward = function () {
        // disable the "Next" or "Finish" button when no option is selected
        if (QuizSteps[quizStepIndex] === Step.questionPage1) {
            if (questionResponses[QuizQuestionId.whereIsTeam]) {
                return true;
            }
        }
        if (QuizSteps[quizStepIndex] === Step.questionPage2) {
            if (questionResponses[QuizQuestionId.whereIsTeam] ===
                WhereIsTeamResponseId.remote &&
                questionResponses[QuizQuestionId.howOften]) {
                // "How To Order" question is not asked when team is remote
                return true;
            }
            if (questionResponses[QuizQuestionId.howToOrder] &&
                questionResponses[QuizQuestionId.howOften]) {
                // Non-remote needs both questions to be answered
                return true;
            }
        }
        if (QuizSteps[quizStepIndex] === Step.questionEmailInput) {
            if (questionResponses[QuizQuestionId.workEmail]) {
                return true;
            }
        }
        return false;
    };
    var onNextOrFinishClick = function () {
        if (QuizSteps[quizStepIndex] === Step.questionPage1) {
            track(QuizEvents.OPTION_RESPONSE_SUBMIT, {
                question: QuizQuestionId.whereIsTeam,
                response: questionResponses[QuizQuestionId.whereIsTeam],
            });
        }
        if (QuizSteps[quizStepIndex] === Step.questionPage2) {
            if (questionResponses[QuizQuestionId.whereIsTeam] ===
                WhereIsTeamResponseId.remote) {
                // "How To Order" question is not asked when team is remote
                track(QuizEvents.OPTION_RESPONSE_SUBMIT, {
                    question: QuizQuestionId.howOften,
                    response: questionResponses[QuizQuestionId.howOften],
                });
            }
            else {
                // Non-remote needs both questions to be answered
                track(QuizEvents.OPTION_RESPONSE_SUBMIT, {
                    question: QuizQuestionId.howToOrder,
                    response: questionResponses[QuizQuestionId.howToOrder],
                });
                track(QuizEvents.OPTION_RESPONSE_SUBMIT, {
                    question: QuizQuestionId.howOften,
                    response: questionResponses[QuizQuestionId.howOften],
                });
            }
        }
        if (QuizSteps[quizStepIndex] === Step.questionEmailInput) {
            setEmailError('');
            // validate email format
            var emailProvided = questionResponses[QuizQuestionId.workEmail];
            var isEmailValid = isEmailFormatValid(emailProvided);
            if (!isEmailValid) {
                // invalid email format
                return setEmailError('Not a valid email');
            }
            // valid email
            track(QuizEvents.EMAIL_SUBMIT, {
                email: questionResponses[QuizQuestionId.workEmail],
            });
        }
        return setQuizStepIndex(quizStepIndex + 1);
    };
    var onOptionSelected = function (questionId, optionId) {
        var _a;
        setQuestionResponses(__assign(__assign({}, questionResponses), (_a = {}, _a[questionId] = optionId, _a)));
    };
    return (React.createElement(FullScreenContainer, null,
        React.createElement(Stack, null,
            React.createElement(Logo, { type: Logo.Types.LogoMark, size: Logo.Sizes.Large })),
        isOnRecommendationStep ? (React.createElement(RecommendationView, { isMobile: isMobile, title: QuizPageStrings.pageTitle.recommendation(), subtitle: QuizPageStrings.subtitle.recommendation(), questionResponses: questionResponses })) : (React.createElement(QuizScreenContainer, null,
            React.createElement(StackChildren, { size: Stack.Sizes.XLarge },
                React.createElement(Text, { tag: "h1", styles: Text.Styles.PageTitle2, textAlign: Text.TextAlignments.Center }, QuizPageStrings.pageTitle.shortQuiz()),
                QuizSteps[quizStepIndex] === Step.questionPage1 && (React.createElement(QuestionContainer, { isMobile: isMobile, question: QuestionsData.WhereIsTeamQuestionData, selectedOption: questionResponses[QuizQuestionId.whereIsTeam], onOptionSelected: onOptionSelected, isAlignCenter: true })),
                QuizSteps[quizStepIndex] === Step.questionPage2 && (React.createElement(StackChildren, { size: Stack.Sizes.XxxLarge },
                    questionResponses[QuizQuestionId.whereIsTeam] !==
                        WhereIsTeamResponseId.remote && (React.createElement(QuestionContainer, { isMobile: isMobile, question: QuestionsData.HowToOrderQuestionData, selectedOption: questionResponses[QuizQuestionId.howToOrder], onOptionSelected: onOptionSelected })),
                    React.createElement(QuestionContainer, { isMobile: isMobile, question: QuestionsData.HowOftenQuestionData, selectedOption: questionResponses[QuizQuestionId.howOften], onOptionSelected: onOptionSelected }))),
                QuizSteps[quizStepIndex] === Step.questionEmailInput && (React.createElement(EmailInputSection, { question: QuestionsData.WorkEmailQuestionData.question, emailLabel: QuestionsData.WorkEmailQuestionData.email.label, emailValue: questionResponses[QuizQuestionId.workEmail], emailError: emailError, onEmailChange: function (emailInput) {
                        return onOptionSelected(QuizQuestionId.workEmail, emailInput);
                    } })),
                React.createElement(ActionAndProgressContainer, null,
                    React.createElement(BackAndProgressContainer, null,
                        hasBack() && (React.createElement(BackContainer, { onClick: onBackClick },
                            React.createElement(Icon, { type: Icon.Types.ArrowLeft, size: Icon.Sizes.Medium, color: Icon.Colors.CurrentColor, accessibilityLabel: "Back" }))),
                        React.createElement(ProgressBar, { currentQuizStep: quizStepIndex, numberOfQuestions: Questions.length })),
                    React.createElement(Button, { size: Button.Sizes.Small, isInline: true, isDisabled: !canMoveForward(), onClick: onNextOrFinishClick }, getButtonText())))))));
};
export default QuizV2;
